import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import Checkout from "./Checkout";
import Loader from "../../component/Loader/Loader";

/**
 * /checkout/:checkoutToken
 *
 * The URL carries a short-lived signed token, never the Stripe client secret.
 * On mount (and on every refresh) the token is exchanged for the secret via
 * GET /api/stripe/checkout-session/:token, and the secret lives only in React
 * state from there. Nothing sensitive reaches the address bar, browser
 * history, access logs or analytics.
 */
const CheckoutWrapper = ({ stripePromise }) => {
  const { checkoutToken } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [session, setSession] = useState(null); // { clientSecret, plan, email, name }
  const [error, setError] = useState(null); // { message }

  // Applying a promotion code rebuilds the subscription server-side, so the
  // Payment Element has to be re-mounted against the new invoice's secret.
  // The route token stays the source of truth (a refresh must not resurrect a
  // cancelled subscription), and the applied code rides along in router state.
  const [promo, setPromo] = useState(location.state?.promo ?? null);

  useEffect(() => {
    let cancelled = false;
    setSession(null);
    setError(null);

    const load = async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}api/stripe/checkout-session/${encodeURIComponent(
          checkoutToken
        )}`;
        const res = await fetch(url, { credentials: "include" });

        let payload = null;
        try {
          payload = await res.json();
        } catch {
          // ignore non-json
        }
        if (cancelled) return;

        if (!res.ok || !payload?.success) {
          setError({
            message:
              payload?.message ||
              "This checkout link is no longer valid. Please choose your plan again.",
          });
          return;
        }

        // Reopened after paying: the backend hands back no secret.
        if (payload.status === "paid") {
          navigate("/payment-successful", {
            replace: true,
            state: { fromCheckout: true },
          });
          return;
        }

        setSession({
          clientSecret: payload.clientSecret,
          plan: payload.plan,
          email: payload.email,
          name: payload.name,
        });
      } catch (err) {
        if (cancelled) return;
        setError({ message: err?.message || "Network error. Please try again." });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [checkoutToken, navigate]);

  const options = useMemo(
    () => ({
      clientSecret: session?.clientSecret,
      appearance: { theme: "stripe" },
    }),
    [session?.clientSecret]
  );

  const handlePromoApplied = (payload) => {
    // A 100%-off code settles the $0 invoice outright — there is no card step.
    if (payload.paid) {
      navigate("/payment-successful", { state: { fromCheckout: true } });
      return;
    }

    const next = payload.promoCode
      ? { promoCode: payload.promoCode, amounts: payload.amounts }
      : null;

    setPromo(next);
    // The rebuilt subscription has a new PaymentIntent, so the URL gets the
    // new token; the effect above re-fetches and re-mounts Elements.
    navigate(`/checkout/${payload.checkoutToken}`, {
      replace: true,
      state: { promo: next },
    });
  };

  if (error) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 p-6 text-center font-inter">
        <p className="text-xl font-medium text-[#1A1A1AE5]">
          Checkout link expired
        </p>
        <p className="max-w-md text-[#1A1A1A99]">{error.message}</p>
        <Link
          to="/pricing"
          className="mt-2 rounded-full bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] px-6 py-2.5 font-semibold text-white"
        >
          Back to pricing
        </Link>
      </div>
    );
  }

  if (!session) {
    return <Loader />;
  }

  return (
    // Stripe does not allow swapping clientSecret on a live Elements instance,
    // so the key forces a fresh one whenever the secret changes.
    <Elements key={session.clientSecret} stripe={stripePromise} options={options}>
      <Checkout
        planKey={session.plan}
        checkoutToken={checkoutToken}
        customerEmail={session.email}
        customerName={session.name}
        promo={promo}
        onPromoApplied={handlePromoApplied}
      />
    </Elements>
  );
};

export default CheckoutWrapper;
